import { useEffect, useState } from "react";
import { Lock, Mail, Phone, ShieldCheck, Copy, Check } from "lucide-react";
import { getSubscriptionBlock, subscribeSubscriptionBlock } from "@/lib/subscriptionGate";
import { getToken } from "@/lib/api";

// Page « accès suspendu » — affichée à la place de TOUTE l'application
// (vitrine comme back-office de la boutique) dès qu'un appel API répond 402
// { code: "subscription_blocked" }. Tout ce qu'elle affiche vient du serveur
// (Plateforme › Abonnements › Page de blocage) : bandeau, titre, message,
// logo, couleur d'accent, libellés des boutons, note, pied de page, et
// l'affichage ou non du nom de la boutique, des dates et de la note à
// l'administrateur. Aucun texte n'est codé ici.
//
// `preview` : rend la page à partir d'un état fourni (aperçu en direct dans
// l'écran d'édition), sans toucher au gate ni au jeton de session.

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch (e) { return ""; }
};

// Corps de réponse 402 → props d'affichage. Exporté pour l'aperçu et les tests.
export function blockPageProps(info) {
  const accent = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(info?.accent || "") ? info.accent : "#8A6116";
  return {
    eyebrow: info?.eyebrow || "",
    title: info?.title || "",
    message: info?.message || "",
    logoUrl: info?.logo_url || "",
    accent,
    emailLabel: info?.email_label || info?.support_email || "",
    phoneLabel: info?.phone_label || info?.support_phone || "",
    footer: info?.footer || "",
    note: info?.note || "",
    adminNote: info?.admin_note || "",
    showShopName: info?.show_shop_name !== false,
    showDates: info?.show_dates !== false,
    shopName: info?.shop_name || "",
    supportEmail: info?.support_email || "",
    supportPhone: info?.support_phone || "",
    since: info?.since || null,
    reopensOn: info?.reopens_on || null,
  };
}

export function BlockPage({ info, isAdmin = false, preview = false }) {
  const p = blockPageProps(info);
  const phoneHref = p.supportPhone ? `tel:${p.supportPhone.replace(/[^\d+]/g, "")}` : null;
  const stop = preview ? (e) => e.preventDefault() : undefined;
  // Copier l'adresse (ou le numéro) : sur un téléphone, ouvrir sa messagerie
  // n'est pas toujours possible — pouvoir coller l'adresse ailleurs l'est.
  const [copied, setCopied] = useState("");
  const copy = async (value, what) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied(""), 2000);
    } catch (e) {
      /* presse-papiers indisponible : le texte reste sélectionnable */
    }
  };
  // Fonction de rendu, pas un composant imbriqué (qui serait remonté à
  // chaque rendu et perdrait l'état « copié »).
  const copyButton = (value, what, label) => (
    <button key={what} type="button" onClick={() => copy(value, what)} title={label} aria-label={label} data-testid={`subscription-blocked-copy-${what}`}
            className="shrink-0 p-2 rounded-md border border-[#c9cccf] bg-white text-[#5c5f62] hover:bg-[#f6f6f7] transition-colors">
      {copied === what ? <Check className="h-4 w-4 text-[#007f5f]" /> : <Copy className="h-4 w-4" />}
    </button>
  );

  return (
    <div className={`bg-[#f4f6f8] text-[#202223] flex items-center justify-center px-6 ${preview ? "py-8" : "min-h-screen py-16"}`} data-testid="subscription-blocked">
      <div className="w-full max-w-lg bg-white border border-[#e1e3e5] rounded-lg shadow-sm p-8 md:p-10 text-center">
        {p.logoUrl ? (
          <img src={p.logoUrl} alt="" className="mx-auto max-h-14 mb-5 object-contain" data-testid="subscription-blocked-logo" />
        ) : (
          <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-5" style={{ background: `${p.accent}1A` }}>
            <Lock className="h-7 w-7" strokeWidth={1.6} style={{ color: p.accent }} />
          </div>
        )}
        {p.eyebrow && <p className="text-[11px] uppercase tracking-[0.2em] text-[#8c9196]">{p.eyebrow}</p>}
        <h1 className="text-2xl md:text-3xl font-semibold mt-2" data-testid="subscription-blocked-title">{p.title}</h1>
        {(p.showShopName && p.shopName) || (p.showDates && (p.since || p.reopensOn)) ? (
          <p className="text-sm text-[#6d7175] mt-1" data-testid="subscription-blocked-meta">
            {p.showShopName && p.shopName ? <>Boutique : <span className="font-medium text-[#202223]">{p.shopName}</span></> : null}
            {p.showDates && p.since ? `${p.showShopName && p.shopName ? " · " : ""}depuis le ${fmtDate(p.since)}` : ""}
            {p.showDates && p.reopensOn ? `${p.showShopName && p.shopName ? " · " : ""}réouverture le ${fmtDate(p.reopensOn)}` : ""}
          </p>
        ) : null}
        {p.message && <p className="text-[15px] text-[#4a4e52] leading-relaxed mt-5" data-testid="subscription-blocked-message">{p.message}</p>}

        <div className="mt-7 space-y-2.5">
          {phoneHref && (
            <div className="flex items-center gap-2">
              <a href={phoneHref} onClick={stop} data-testid="subscription-blocked-phone"
                 className="flex-1 flex items-center justify-center gap-2 text-white rounded-md px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity"
                 style={{ background: p.accent }}>
                <Phone className="h-4 w-4" /> {p.phoneLabel}{p.phoneLabel !== p.supportPhone ? ` · ${p.supportPhone}` : ""}
              </a>
              {copyButton(p.supportPhone, "phone", "Copier le numéro")}
            </div>
          )}
          {p.supportEmail && (
            <div className="flex items-center gap-2">
              <a href={`mailto:${p.supportEmail}?subject=${encodeURIComponent(`Finalisation du contrat — ${p.shopName || "ma boutique"}`)}`}
                 onClick={stop} data-testid="subscription-blocked-email"
                 className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#c9cccf] rounded-md px-5 py-3 text-sm font-medium text-[#202223] hover:bg-[#f6f6f7] transition-colors">
                <Mail className="h-4 w-4" /> {p.emailLabel}
              </a>
              {copyButton(p.supportEmail, "email", "Copier l'adresse e-mail")}
            </div>
          )}
          {copied && <p className="text-xs text-[#007f5f]" role="status" data-testid="subscription-blocked-copied">{copied === "email" ? "Adresse copiée." : "Numéro copié."}</p>}
        </div>
        {p.note && <p className="mt-4 text-xs text-[#6d7175]" data-testid="subscription-blocked-note">{p.note}</p>}

        {isAdmin && p.adminNote && (
          <p className="mt-6 text-xs text-[#6d7175] flex items-start gap-2 text-left bg-[#f6f6f7] rounded-md p-3" data-testid="subscription-blocked-admin-note">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-[#007f5f]" />
            <span>{p.adminNote}</span>
          </p>
        )}
        {p.footer && <p className="mt-6 text-[11px] text-[#8c9196]">{p.footer}</p>}
      </div>
    </div>
  );
}

export default function SubscriptionBlocked() {
  const [info, setInfo] = useState(getSubscriptionBlock());
  useEffect(() => subscribeSubscriptionBlock(setInfo), []);
  if (!info) return null;
  // L'admin de la boutique était connecté : la note qui lui est destinée
  // (ses données sont conservées) n'a de sens que pour lui.
  return <BlockPage info={info} isAdmin={!!getToken()} />;
}
